<template>
  <div class="container">
    <div class="text-center mt-4" v-if="isLoading">
      <HMSpinner />
    </div>
    <div v-if="meeting" class="booked-meeting-container">
      <div class="col-12 text-center mt-4">
        <HMInstructorsStack
          :instructors="meeting.meeting_type.instructors"
          :size="'65px'"
          :clickable="false"
        />
        <div class="mt-3 c-charcoal">
          {{ instructorNames }}
        </div>
      </div>
      <div class="col-12 mt-3">
        <h2 v-if="!meeting.is_cancelled" class="text-center">
          {{ $t('MARKETING.TITLES.BOOKING_CONFIRMED') }}
        </h2>
        <HMVueEditorPreview
          v-if="meeting.meeting_type.confirmation_message"
          :html="meeting.meeting_type.confirmation_message"
          class="mt-3"
        />
      </div>
      <div class="row my-2">
        <div class="col-12">
          <hr />
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-12">
          <div :class="{ 'line-through': meeting.is_cancelled }">
            <i class="fa fa-calendar-o fa-fw mr-2 c-brand-color"></i>
            {{ $formatDate(meeting.start_time, 'date-word') }}
          </div>
          <div class="mt-2" :class="{ 'line-through': meeting.is_cancelled }">
            <i class="fa fa-clock-o fa-fw mr-2 c-brand-color"></i>
            {{ startTime }} - {{ endTime }}
          </div>
          <div class="mt-2 text-truncate" v-if="meeting.event">
            <i class="fa fa-link fa-fw mr-2 c-brand-color"></i>
            <a
              class="undecorated-router-link"
              :href="meeting.event.zoom_join_url"
              >{{ meeting.event.zoom_join_url }}</a
            >
          </div>
          <div class="mt-2" v-else>
            <i class="far fa-times fa-fw mr-2 c-brand-color"></i>
            {{ $t('MARKETING.TEXT.MEETING_IS_CANCELLED') }}
          </div>
          <div class="mt-5" v-if="!meeting.is_cancelled">
            <HMButton
              v-if="meeting.meeting_type.allow_reschedule"
              :disabled="isExpired"
              class="mr-4"
              color="brand"
              @click="showRescheduleModal"
            >
              <i class="fas fa-repeat c-white mr-2"></i>
              {{ $t('MARKETING.BUTTONS.RESCHEDULE_MEETING') }}
            </HMButton>
            <HMButton
              v-if="meeting.meeting_type.allow_cancel"
              :disabled="isExpired"
              color="outline-brand"
              @click="showCancelMeetingModal"
            >
              <i class="far fa-times c-brand-color mr-2"></i>
              {{ $t('MARKETING.BUTTONS.CANCEL_MEETING') }}
            </HMButton>
          </div>
        </div>
      </div>
      <div
        class="row mt-2"
        v-if="meeting.meeting_type.confirmation_media && !meeting.is_cancelled"
      >
        <div class="col-12 my-4">
          <hr />
        </div>
        <div class="col-12">
          <HMFilePreview
            :file="meeting.meeting_type.confirmation_media.media"
          />
        </div>
      </div>
      <div class="row my-4">
        <div class="col-12">
          <hr />
        </div>
        <div class="col-12" v-if="!meeting.is_cancelled">
          {{ $t('MARKETING.TEXT.CALENDAR_INVITE_SENT') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import Vue from 'vue';
import { getStudioURL } from '../../router/custom-domain-utils';
import moment from 'moment';
import HMDialog from '../../components/common/HMDialog';
import MeetingRescheduleModal from '../../components/marketing/MeetingRescheduleModal';
import HMVueEditorPreview from '@/web/components/common/HMVueEditorPreview.vue';

export default Vue.extend({
  name: 'StudioBookedMeeting',
  components: { HMVueEditorPreview },
  computed: {
    ...mapGetters({}),
    studioURL() {
      return getStudioURL(this.$route);
    },
    startTime() {
      return this.$formatDate(this.meeting?.start_time, 'time');
    },
    endTime() {
      return this.$formatDate(
        moment(this.meeting?.start_time)
          .add(this.meeting?.duration_minutes, 'minutes')
          .toDate(),
        'time'
      );
    },
    isExpired() {
      if (!this.meeting) return false;
      const now = moment();
      const endOfMeeting = moment(this.meeting?.start_time).add(
        this.meeting?.duration_minutes,
        'minutes'
      );

      return now.isAfter(endOfMeeting);
    },
    instructorNames() {
      return this.meeting?.meeting_type?.instructors
        .map(i => i.name)
        .join(', ');
    },
  },
  data() {
    return {
      isLoading: true,
      meeting: null,
      isRescheduling: false,
    };
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        this.meeting = await this.$store.dispatch(
          'marketing/getPublicMeeting',
          {
            studioURL: this.studioURL,
            meetingUniqueId: this.$route.params.meeting_unique_id,
          }
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Could not load this meeting',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
    showRescheduleModal() {
      this.$modal.show(
        MeetingRescheduleModal,
        {
          meetingType: this.meeting.meeting_type,
          studioURL: this.studioURL,
          currentDateTime: moment(this.meeting.start_time).toDate(),
          onMeetingRescheduled: async (newSelectedDate, timezone) => {
            await this.reschedule(newSelectedDate, timezone);
          },
        },
        {
          classes: ['vm--booking-modal'],
        }
      );
    },
    async reschedule(newSelectedDate, timezone) {
      this.isRescheduling = true;
      try {
        await this.$store.dispatch('marketing/reschedulePublicMeeting', {
          studioURL: this.studioURL,
          meetingUniqueId: this.$route.params.meeting_unique_id,
          newStartTime: newSelectedDate,
          timezone,
        });
        await this.loadData();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to reschedule meeting',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isRescheduling = false;
      }
    },
    async showCancelMeetingModal() {
      this.$modal.show(HMDialog, {
        title: this.$t('MARKETING.TITLES.CANCEL_MEETING'),
        text: this.$t('MARKETING.TEXT.CONFIRM_CANCEL_MEETING', {
          fullName: this.instructorNames,
        }),
        confirmButton: this.$t('GENERAL.BUTTON.CANCEL_MEETING'),
        confirm: async () => {
          await this.cancelMeeting();
        },
      });
    },
    async cancelMeeting() {
      try {
        await this.$store.dispatch('marketing/cancelPublicMeeting', {
          studioURL: this.studioURL,
          meetingUniqueId: this.$route.params.meeting_unique_id,
        });
        this.loadData();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to cancel meeting',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
  },
  async mounted() {
    await this.loadData();
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.booked-meeting-container {
  max-width: 768px;
  margin: 0 auto;
}

.line-through {
  text-decoration: line-through;
}
</style>

<style lang="scss">
.vm--booking-modal {
  max-width: 1280px !important;
}
</style>
